import React from 'react';
import Icon from '../../../components/AppIcon';

const ExecutiveSummaryTab = ({ dateRange }) => {
  const kpiData = [
    {
      module: 'Dashboard',
      title: 'Business Health Index',
      value: 87,
      maxValue: 100,
      trend: 'up',
      trendValue: 5.2,
      icon: 'Activity',
      color: 'primary',
      description: 'Overall business performance score'
    },
    {
      module: 'Business Model Canvas',
      title: 'Strategy Completion',
      value: 78,
      maxValue: 100,
      trend: 'up',
      trendValue: 12.5,
      icon: 'Grid3x3',
      color: 'secondary',
      description: '7 of 9 blocks completed'
    },
    {
      module: 'Predictive Analysis',
      title: 'Forecast Accuracy',
      value: 92,
      maxValue: 100,
      trend: 'up',
      trendValue: 3.8,
      icon: 'TrendingUp',
      color: 'accent',
      description: 'Revenue prediction reliability'
    },
    {
      module: 'Goal Intelligence',
      title: 'Goal Achievement',
      value: 68,
      maxValue: 100,
      trend: 'neutral',
      trendValue: 0.5,
      icon: 'Target',
      color: 'warning',
      description: 'Quarterly milestone progress'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      primary: { bg: 'bg-primary/10', text: 'text-primary', icon: 'var(--color-primary)' },
      secondary: { bg: 'bg-secondary/10', text: 'text-secondary', icon: 'var(--color-secondary)' },
      accent: { bg: 'bg-accent/10', text: 'text-accent', icon: 'var(--color-accent)' },
      warning: { bg: 'bg-warning/10', text: 'text-warning', icon: 'var(--color-warning)' }
    };
    return colors?.[color] || colors?.primary;
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return 'TrendingUp';
    if (trend === 'down') return 'TrendingDown';
    return 'Minus';
  };

  const getTrendColor = (trend) => {
    if (trend === 'up') return 'text-success';
    if (trend === 'down') return 'text-error';
    return 'text-muted-foreground';
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {kpiData?.map((kpi, index) => {
          const colorClasses = getColorClasses(kpi?.color);
          const percentage = (kpi?.value / kpi?.maxValue) * 100;

          return (
            <div
              key={index}
              className="bg-card border border-border rounded-lg p-3 md:p-4 elevation-sm hover:elevation-md transition-smooth"
            >
              <div className="flex items-start justify-between mb-2 md:mb-3">
                <div className={`w-9 h-9 md:w-10 md:h-10 rounded-lg ${colorClasses?.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon name={kpi?.icon} size={16} className="md:w-[18px] md:h-[18px]" color={colorClasses?.icon} />
                </div>
                <div className={`flex items-center gap-1 ${getTrendColor(kpi?.trend)} flex-shrink-0`}>
                  <Icon name={getTrendIcon(kpi?.trend)} size={12} className="md:w-[14px] md:h-[14px]" />
                  <span className="text-xs font-medium font-caption">
                    {Math.abs(kpi?.trendValue)}%
                  </span>
                </div>
              </div>
              <h3 className="text-xs md:text-sm font-medium text-muted-foreground font-caption mb-1 break-words">
                {kpi?.module}
              </h3>
              <p className="text-sm md:text-lg font-bold text-foreground font-heading mb-1 break-words">
                {kpi?.title}
              </p>
              <div className="flex items-baseline gap-2 mb-2 md:mb-3">
                <span className={`text-xl md:text-2xl font-bold font-heading ${colorClasses?.text}`}>
                  {kpi?.value}
                </span>
                <span className="text-xs md:text-sm text-muted-foreground font-body">
                  / {kpi?.maxValue}
                </span>
              </div>
              <div className="relative h-1.5 bg-muted rounded-full overflow-hidden mb-2">
                <div
                  className={`absolute inset-y-0 left-0 rounded-full transition-smooth ${colorClasses?.bg?.replace('/10', '')}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground font-caption break-words">
                {kpi?.description}
              </p>
            </div>
          );
        })}
      </div>

      {/* Key Insights Summary */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
        <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Lightbulb" size={18} className="md:w-5 md:h-5" color="var(--color-primary)" />
          </div>
          <h3 className="text-base md:text-lg font-semibold text-foreground font-heading">
            Executive Insights
          </h3>
        </div>
        <div className="space-y-2 md:space-y-3">
          <div className="flex items-start gap-2 md:gap-3 p-2.5 md:p-3 bg-success/10 border border-success/20 rounded-lg">
            <Icon name="CheckCircle" size={14} className="md:w-4 md:h-4 flex-shrink-0" color="var(--color-success)" />
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm font-medium text-foreground font-body break-words">
                Strong Overall Performance
              </p>
              <p className="text-xs text-muted-foreground font-caption mt-1 break-words">
                Business Health Index at 87/100 with consistent upward trend across all metrics
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2 md:gap-3 p-2.5 md:p-3 bg-warning/10 border border-warning/20 rounded-lg">
            <Icon name="AlertTriangle" size={14} className="md:w-4 md:h-4 flex-shrink-0" color="var(--color-warning)" />
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm font-medium text-foreground font-body break-words">
                Goal Achievement Needs Attention
              </p>
              <p className="text-xs text-muted-foreground font-caption mt-1 break-words">
                Current progress at 68% - recommend reviewing quarterly milestones and resource allocation
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2 md:gap-3 p-2.5 md:p-3 bg-accent/10 border border-accent/20 rounded-lg">
            <Icon name="TrendingUp" size={14} className="md:w-4 md:h-4 flex-shrink-0" color="var(--color-accent)" />
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm font-medium text-foreground font-body break-words">
                Excellent Forecast Accuracy
              </p>
              <p className="text-xs text-muted-foreground font-caption mt-1 break-words">
                Predictive models showing 92% accuracy - reliable foundation for strategic planning
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveSummaryTab;